import type { EnhancedLine } from './parseExampleNoteIntoSections';

/**
 * Common letter-style salutations that appear at the end of the BE-served
 * per-specialty document body in `/onboarding-v2-assets`. Match is
 * case-insensitive and tolerates an optional trailing comma so authors can
 * write "Kind regards," or "Kind regards" interchangeably.
 *
 * Today the seeded data only uses "Kind regards,"; the rest are listed
 * defensively so a one-off authoring change in ml-scribe doesn't leak the
 * specialty-name signature back into the rendered referral letter.
 */
const SALUTATION_PATTERNS: readonly RegExp[] = [
  /^kind\s+regards,?$/i,
  /^yours\s+sincerely,?$/i,
  /^yours\s+faithfully,?$/i,
  /^best\s+regards,?$/i,
  /^warm\s+regards,?$/i,
  /^best\s+wishes,?$/i,
  /^sincerely,?$/i,
  /^regards,?$/i,
];

function isSalutation(line: EnhancedLine | undefined): boolean {
  if (!line) return false;
  return SALUTATION_PATTERNS.some((pattern) => pattern.test(line.text.trim()));
}

/**
 * Strip the trailing specialty signature ("…\nAcupuncturist",
 * "…\nAddiction Medicine Specialist") that the BE seeds at the end of
 * every per-specialty document body in `/onboarding-v2-assets`. The
 * "Kind regards," salutation is intentionally KEPT — the renderer
 * appends the user's actual name as the next line so the document reads:
 *
 *   …
 *   Kind regards,
 *   Jay Tay
 *
 * instead of being impersonally signed by the specialty title.
 *
 * The BE-served shape after parsing looks like:
 *
 *   [..., [{header: 'Kind regards,'}], [{header: 'Acupuncturist'}]]
 *
 * (each header starts a new section in `parseExampleNoteIntoSections`),
 * but we also handle the case where the salutation + signature live
 * inside the same section as preceding body content. In every case the
 * salutation is preserved and only the lines AFTER it are dropped. If no
 * salutation is found the input is returned unchanged so non-letter
 * documents (notes, summaries, plans without a closing) are unaffected.
 */
export function stripDocumentSignOff(sections: EnhancedLine[][]): EnhancedLine[][] {
  if (sections.length === 0) return sections;

  // Walk from the end so a salutation found in a later section wins over
  // any pre-existing body text. Within each section we check every line:
  // the typical shape has the salutation as the only line in its section,
  // but a single-section document can also glue salutation + signature
  // onto the same section as preceding body content.
  for (let i = sections.length - 1; i >= 0; i -= 1) {
    const section = sections[i]!;
    const salutationIdx = indexOfSalutationLine(section);
    if (salutationIdx < section.length) {
      // Keep the salutation line itself (drop only the lines after it),
      // and discard any later sections in their entirety — those are the
      // BE-served specialty signature.
      const head = sections.slice(0, i);
      const preserved = section.slice(0, salutationIdx + 1);
      head.push(preserved);
      return head;
    }
  }

  return sections;
}

function indexOfSalutationLine(section: EnhancedLine[]): number {
  for (let i = 0; i < section.length; i += 1) {
    if (isSalutation(section[i])) return i;
  }
  return section.length;
}

/**
 * True when the document has a letter-style salutation that the renderer
 * should follow with a personalised signature line.
 */
export function hasSalutation(sections: EnhancedLine[][]): boolean {
  for (const section of sections) {
    for (const line of section) {
      if (isSalutation(line)) return true;
    }
  }
  return false;
}
